import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRefetchOn } from "@/hooks/useRefetchOn";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { BackButton } from "@/common/BackButton";
import { Box, Stack, Typography, Collapse } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import ipponApi from "@/config/axios";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  CustomTextInput,
  CustomSingleSelect,
  CustomCalendarInput,
  CustomSectionTitle,
  CustomCategorySelectSingle,
  CustomModal,
  CustomFileUploader,
  CustomLoading,
  FixedActionBar,
  CustomButton,
  AiBadge,
  colors,
  useScrollToFirstError,
  CustomFormNavigationGuard,
  usePreviewDrawerUnsaved,
} from "@ippon/shared-components";
import { formatDateForInput } from "@ippon/shared-utils";
import type { AppDispatch } from "@/store";
import {
  fetchExpenseById,
  updateExpense,
  createExpense,
  type ExpenseRecord,
  type ExpenseBreakdownItem,
  type ExpenseCustomFieldValue,
} from "@/store/expenseSlice";
import {
  createBudgetCategory,
  fetchBudgetCategories,
} from "@/store/budgetSlice";
import { Can } from "@/components/auth/Can";
import ExpenseBreakdownTable from "@/components/PropertyDetail/ExpenseManagement/ExpenseBreakdownTable";
import ExpenseAmountSummary from "@/components/PropertyDetail/ExpenseManagement/ExpenseAmountSummary";
import { isBillingAmountTooLong } from "@/components/PropertyDetail/shared/BillingItemsTable";
import ExpenseCustomFieldsAddable, {
  type PendingExpenseCustomField,
} from "@/components/PropertyDetail/ExpenseManagement/ExpenseCustomFieldsAddable";
import { createExpenseCustomField } from "@/store/expenseCustomFieldSlice";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import { useExpenseBudgetCategories } from "@/hooks/useExpenseBudgetCategories";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";

const normalizeCustomFieldValue = (value: ExpenseCustomFieldValue["value"]) => {
  if (value == null) return "";
  if (Array.isArray(value)) return [...value].sort();
  return value;
};

const isEmptyCustomFieldValue = (
  value: ReturnType<typeof normalizeCustomFieldValue>,
) => {
  if (Array.isArray(value)) return value.length === 0;
  return value === "";
};

const serializeExpenseCustomFields = (
  customFieldValues: ExpenseCustomFieldValue[],
  pendingCustomFields: PendingExpenseCustomField[],
) =>
  JSON.stringify({
    customFieldValues: customFieldValues
      .map((v) => ({
        field_id: v.field_id,
        value: normalizeCustomFieldValue(v.value),
      }))
      .filter((v) => !isEmptyCustomFieldValue(v.value))
      .sort((a, b) => a.field_id.localeCompare(b.field_id)),
    pendingCustomFields: pendingCustomFields
      .map((v) => ({
        temp_id: v.temp_id,
        label: v.label.trim(),
        value: v.value,
      }))
      .filter((v) => v.label || v.value)
      .sort((a, b) => a.temp_id.localeCompare(b.temp_id)),
  });

interface Props {
  /** ドロワー等で埋め込む際に使用する expense_id (URL パラメータより優先) */
  overrideExpenseId?: string;
  /** 新規作成モード (/expenses/new) */
  mode?: "new" | "edit";
  /** 新規時に指定する property_id */
  overridePropertyId?: string;
  /** 新規時に紐付ける budget_category_id (予実→支出リンク) */
  presetBudgetCategoryId?: string;
  /**
   * 新規時のデフォルト expense_type。
   * 予実テーブルの CAPEX/OPEX 行から開いた場合に自動セットされる。
   */
  presetExpenseType?: "OPEX" | "CAPEX";
  /** 新規時のデフォルトステータス。未指定なら通常登録の初期値を使う。 */
  presetStatus?: string;
  /** 新規作成後に呼ばれるコールバック (親でリフレッシュ等に使う) */
  onCreated?: (expenseId: string) => void;
  /** 編集成功後に呼ばれるコールバック (親で予実等を再取得する用途) */
  onUpdated?: (expenseId: string) => void;
  /** 支出データロード時に呼ばれる (ドロワー埋め込み時にタイトル等の取得に使う) */
  onLoaded?: (expense: ExpenseRecord) => void;
  /** 未保存(dirty)状態の変化を親へ通知 (ドロワーの閉じ確認に使う) */
  onDirtyChange?: (dirty: boolean) => void;
  embedded?: boolean;
  /** registerModal 埋め込み時の戻る/キャンセル */
  onCancel?: () => void;
  /**
   * OCR で抽出された値の初期値 (新規時のみ有効)。
   * 与えられた key だけ defaultValues を上書きする。
   */
  presetOcrValues?: Partial<{
    name: string;
    category: string;
    expense_type: "OPEX" | "CAPEX";
    amount: string;
    payee: string;
    partner_name: string;
    tax_rate: string;
    tax_amount: string;
    registration_number: string;
    notes: string;
  }>;
  /** AI が自動入力した field 名 (UI で AI バッジ表示用) */
  presetAiFilledFields?: string[];
  /** OCR モーダル上部に出す警告 (複数支出検出 等) */
  presetOcrWarnings?: string[];
  /** OCR 抽出済の内訳 (breakdown)。税区分付き。 */
  presetBreakdown?: {
    item: string | null;
    amount: number | null;
    tax_category?: "taxable" | "non_taxable";
  }[];
  /** OCR でアップロード済のドキュメント (登録時に書類欄に紐付ける) */
  presetOcrDocument?: {
    document_id: string;
    s3_key: string;
    /** FE プレビュー用 signed URL (BE 発行) */
    signed_url: string;
    file_name: string;
    file_type: string;
  };
}

interface FormValues {
  name: string;
  /** 明細項目 = budget_category_id。 */
  category: string;
  expense_type: "CAPEX" | "OPEX";
  amount: string;
  /** 任意。1 支出あたりの予算 */
  budget: string;
  date: string;
  status: string;
  description: string;
  target_type: string;
  target_id: string;
  /** 紐付け先の修繕 ID (任意) */
  linked_repair_id: string;
  documents: string[];
  // 帳簿要件 (OCR 抽出対象)
  payee: string;
  partner_name: string;
  tax_rate: string;
  tax_amount: string;
  registration_number: string;
}

const LABEL_WIDTH = "120px";
const INPUT_WIDTH = "600px";

const EXPENSE_TYPE_OPTIONS = [
  { value: "OPEX", label: "OPEX（運営費用）" },
  { value: "CAPEX", label: "CAPEX（資本的支出）" },
];

const STATUS_OPTIONS = [
  { value: "下書き", label: "下書き" },
  { value: "AI作成", label: "AI作成" },
  { value: "支払予定", label: "支払予定" },
  { value: "未払い", label: "未払い" },
  { value: "支払済", label: "支払済" },
  { value: "要確認", label: "要確認" },
  { value: "保留", label: "保留" },
  { value: "その他", label: "その他" },
];

// 物件構成 (Unit) の area_type と完全一致。
// BE: ExpenseTargetType は "building" も型として残るが UI では選ばせない方針。
const TARGET_TYPE_OPTIONS = [
  { value: "", label: "指定しない" },
  { value: "private", label: "専有部" },
  { value: "common", label: "共用部" },
  { value: "facility", label: "設備" },
  { value: "land", label: "敷地" },
  { value: "other", label: "その他" },
];

// 課税行に適用する税率 (収入と同じ「課税10%」)。
const BREAKDOWN_TAX_RATE = 10;

const genBreakdownId = (i = 0): string =>
  `bd_${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${i}`;

/**
 * 内訳 (明細) の初期化。新モデルでは「明細の合計＝支払金額」なので、
 * 明細が無い場合 (OCR が明細を取れなかった / 旧データに明細が無い) は
 * 既存の金額から「一式」1 行を合成し、合計が元の支払金額と一致するようにする。
 *
 * @param presetBreakdown OCR 抽出済の明細 (税抜)
 * @param fallbackAmount  明細が無い時に一式の原資にする金額 (税込支払総額)
 * @param fallbackTax     消費税額 (一式は税抜にするため amount から差し引く)
 */
function buildInitialBreakdown(params: {
  presetBreakdown?: {
    item: string | null;
    amount: number | null;
    tax_category?: "taxable" | "non_taxable";
  }[];
  fallbackAmount?: number | null;
  fallbackTax?: number | null;
}): ExpenseBreakdownItem[] {
  const { presetBreakdown, fallbackAmount, fallbackTax } = params;
  if (presetBreakdown && presetBreakdown.length > 0) {
    return presetBreakdown.map((b, i) => ({
      item_id: genBreakdownId(i),
      item: b.item ?? "",
      amount: typeof b.amount === "number" ? b.amount : 0,
      // OCR が課税/非課税を返さなければ課税扱い
      tax_category: b.tax_category ?? "taxable",
    }));
  }
  // 明細なし + 金額あり → 税抜一式を 1 行合成 (合計が元の税込額に一致)。
  // 税込との差(消費税)があれば課税、無ければ非課税として一式を作る。
  if (typeof fallbackAmount === "number" && fallbackAmount > 0) {
    const tax = typeof fallbackTax === "number" ? fallbackTax : 0;
    const net = Math.max(0, Math.round(fallbackAmount - tax));
    return [
      {
        item_id: genBreakdownId(),
        item: "一式",
        amount: net,
        tax_category: tax > 0 ? "taxable" : "non_taxable",
      },
    ];
  }
  // 完全な新規 (金額も無い) → 空行 1 行スタート
  return [
    { item_id: genBreakdownId(), item: "", amount: 0, tax_category: "taxable" },
  ];
}

/**
 * 明細を「内容のみ(item_id は除外)」で安定文字列化。
 * 更新ボタンの活性判定で、初期スナップショットと現在値を比較するのに使う。
 * item_id は生成値で内容と無関係なので除外し、順序は保持する。
 */
function serializeBreakdown(rows: ExpenseBreakdownItem[]): string {
  return JSON.stringify(
    rows.map((r) => [
      (r.item || "").trim(),
      Number(r.amount) || 0,
      r.tax_category ?? "taxable",
    ]),
  );
}

export const ExpenseDetailPage: React.FC<Props> = ({
  overrideExpenseId,
  mode,
  overridePropertyId,
  presetBudgetCategoryId,
  presetExpenseType,
  presetStatus,
  onCreated,
  onUpdated,
  onLoaded,
  onDirtyChange,
  embedded,
  onCancel,
  presetOcrValues,
  presetAiFilledFields,
  presetOcrWarnings,
  presetBreakdown,
  presetOcrDocument,
}) => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const { showToast } = useGlobalToast();

  const expenseId =
    overrideExpenseId || (mode === "new" ? "" : params?.id || "");
  const isNew = mode === "new" || !expenseId;

  // URL クエリ (?property_id=xxx&budget_category_id=yyy) を
  // props が無いときのフォールバックとして使う
  const queryPropertyId = searchParams.get("property_id") || "";
  const queryBudgetCategoryId = searchParams.get("budget_category_id") || "";
  const effectivePresetBudgetCategoryId =
    presetBudgetCategoryId || queryBudgetCategoryId || undefined;

  const [data, setData] = useState<ExpenseRecord | null>(null);
  const dataRef = useRef<ExpenseRecord | null>(null);
  // onLoaded を ref 経由で呼ぶ (収入詳細と同じ)。インライン関数の prop でも
  // fetchDetail の identity を変えず、ステータス即PATCH等の再レンダーで
  // useEffect([fetchDetail]) が誤発火して画面が再フェッチされるのを防ぐ。
  const onLoadedRef = useRef(onLoaded);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);
  const [loading, setLoading] = useState(false);
  const [initialLoadError, setInitialLoadError] = useState<ApiErrorType | null>(
    null,
  );
  // RHF 外の補助 state 用。カスタム項目は別途スナップショット比較で判定する。
  const [localDirty, setLocalDirty] = useState(false);
  const [propertyId, setPropertyId] = useState<string>(
    overridePropertyId || queryPropertyId || "",
  );
  const [breadcrumbPropertyName, setBreadcrumbPropertyName] = useState(
    () =>
      ((location.state as Record<string, unknown> | null)?.propName as
        | string
        | undefined) || "",
  );
  const [submitting, setSubmitting] = useState(false);

  // 新規登録モード時、OCR や予実からのプリセットで意味のある初期値が入っているかどうか。
  // defaultValues に直接プリセットを流し込むパスでは isDirty が立たないため、
  // 「初期表示でも内容がある」を別軸で判定して登録ボタンの活性を決める。
  const hasInitialPrefill =
    mode === "new" &&
    ((!!presetOcrValues &&
      Object.values(presetOcrValues).some((v) => v != null && v !== "")) ||
      !!presetBreakdown?.length ||
      !!presetOcrDocument);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const {
    register,
    control,
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<FormValues>({
    defaultValues: {
      // category は BudgetCategory.category_id を保持。
      // 初期値は preset id があればそれを優先、無ければカテゴリ一覧 fetch 後に
      // useEffect で名前から id を解決して埋める。
      name: presetOcrValues?.name ?? "",
      category:
        presetOcrValues?.category ?? effectivePresetBudgetCategoryId ?? "",
      expense_type:
        presetOcrValues?.expense_type ?? presetExpenseType ?? "OPEX",
      amount: presetOcrValues?.amount ?? "",
      budget: "",
      // 発生日は OCR 抽出対象外。新規登録時は常に当日を初期表示する。
      // (編集モードは後段の fetchDetail → reset で rec.date に上書きされる)
      date: formatDateForInput(new Date()),
      status: presetOcrValues ? "AI作成" : (presetStatus ?? "支払予定"),
      description: presetOcrValues?.notes ?? "",
      target_type: "",
      target_id: "",
      linked_repair_id: "",
      // 「その他関連書類」初期値: 既存 documents のみ (OCR 書類は ai_documents 側で扱う)
      documents: [],
      payee: presetOcrValues?.payee ?? "",
      partner_name: presetOcrValues?.partner_name ?? "",
      tax_rate: presetOcrValues?.tax_rate ?? "",
      tax_amount: presetOcrValues?.tax_amount ?? "",
      registration_number: presetOcrValues?.registration_number ?? "",
    },
  });

  // AI 自動入力 field の状態 (UI バッジ表示用)。ユーザーが手動編集すると false にする。
  const [aiFilledFields, setAiFilledFields] = useState<Record<string, boolean>>(
    () => {
      if (!presetAiFilledFields || presetAiFilledFields.length === 0) return {};
      const init: Record<string, boolean> = {};
      for (const k of presetAiFilledFields) init[k] = true;
      return init;
    },
  );
  // 金額桁オーバー時に内訳テーブルへスクロールするための ref。
  const breakdownRef = useRef<HTMLDivElement>(null);
  // 明細が空 (合計 0) のときのインライン必須エラー。トーストではなくテーブル直下に出す。
  const [breakdownEmptyError, setBreakdownEmptyError] = useState(false);
  // 内訳 (form 配列扱い) — RHF の外で controlled。OCR 由来行は AI フラグ ON で初期化。
  // 新規 / OCR どちらのケースも「常に最低 1 行」表示する方針なので、空ならデフォルト空行を入れる。
  const [breakdown, setBreakdown] = useState<ExpenseBreakdownItem[]>(() =>
    buildInitialBreakdown({
      presetBreakdown,
      // OCR が明細を取れなかった時は amount(税込) − tax_amount から税抜一式を合成
      fallbackAmount: presetOcrValues?.amount
        ? Number(presetOcrValues.amount)
        : undefined,
      fallbackTax: presetOcrValues?.tax_amount
        ? Number(presetOcrValues.tax_amount)
        : undefined,
    }),
  );
  // カスタムフォーム (グローバル定義値 + ローカル項目)
  const [customFieldValues, setCustomFieldValues] = useState<
    ExpenseCustomFieldValue[]
  >([]);
  const [pendingCustomFields, setPendingCustomFields] = useState<
    PendingExpenseCustomField[]
  >([]);
  const initialCustomFieldsSerializedRef = useRef<string>(
    serializeExpenseCustomFields([], []),
  );
  const customFieldsDirty =
    serializeExpenseCustomFields(customFieldValues, pendingCustomFields) !==
    initialCustomFieldsSerializedRef.current;
  // 更新ボタンの活性判定用: 「クリーン状態(読込/保存直後)」の明細スナップショット。
  // 現在の明細と内容比較し、元に戻れば非活性・実際に変われば活性、と正しく追従させる。
  const initialBreakdownSerializedRef = useRef<string | null>(null);
  if (initialBreakdownSerializedRef.current === null) {
    initialBreakdownSerializedRef.current = serializeBreakdown(breakdown);
  }
  const breakdownDirty =
    serializeBreakdown(breakdown) !== initialBreakdownSerializedRef.current;
  // CustomFileUploader は API 初期値を表示用オブジェクトへ同期するため、
  // ユーザー操作がなくても documents が RHF 上 dirty になる場合がある。
  // 書類の実操作は onImageChange -> localDirty で判定する。
  const hasFormDirty =
    isDirty && Object.keys(dirtyFields).some((key) => key !== "documents");
  // 親 (ドロワー等) へ未保存状態を通知 (閉じ確認に使う)。
  const combinedDirty =
    hasFormDirty || localDirty || breakdownDirty || customFieldsDirty;
  useEffect(() => {
    onDirtyChange?.(combinedDirty);
  }, [combinedDirty, onDirtyChange]);
  // PreviewDrawer 内で開かれている場合、閉じ時の破棄確認用に dirty を報告 (外では no-op)。
  usePreviewDrawerUnsaved(combinedDirty);
  const [
    breakdownAmountRequiredErrorItemIds,
    setBreakdownAmountRequiredErrorItemIds,
  ] = useState<Set<string>>(() => new Set());
  const [aiBreakdownIds, setAiBreakdownIds] = useState<Set<string>>(() => {
    // breakdown 初期化と同じロジックで item_id を生成。
    // ※ 実際の Set は breakdown state の初期 item_id とは異なる ID になるが、
    //   useEffect で 1 回だけ同期する。
    return new Set();
  });
  // breakdown 初期化済の item_id を AI フラグ Set へ反映 (1 回だけ)
  useEffect(() => {
    if (presetBreakdown && presetBreakdown.length > 0 && breakdown.length > 0) {
      setAiBreakdownIds(new Set(breakdown.map((b) => b.item_id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBreakdownAmountRequiredErrorItemIds = (
    rows: ExpenseBreakdownItem[],
  ): Set<string> => {
    // 金額は ※必須。どの行でも金額が未入力(0以下)ならエラーにする。
    // ※以前は「項目名も金額も空の行」を素通りさせ、全行が空のときだけ
    //   先頭行のみフォールバックで弾いていたため、1件目は弾くのに
    //   2件目以降の空行は更新できてしまう非対称があった。
    return new Set(
      rows
        .filter((row) => Number(row.amount || 0) <= 0)
        .map((row) => row.item_id),
    );
  };

  // 小計 (明細合計・税抜)。「支払金額」の手入力は廃止し、
  // 合計(税込) = 小計 + 消費税 を常に明細から自動算出する。
  const subtotal = useMemo(
    () =>
      breakdown.reduce(
        (sum, b) => sum + (typeof b.amount === "number" ? b.amount : 0),
        0,
      ),
    [breakdown],
  );
  // 消費税 = Σ(課税行の税抜金額) × 10% (収入と同じ)。
  // 税区分は明細の各行 (tax_category) が持つ。未指定は課税扱い。非課税行は除外。
  const taxAmount = useMemo(
    () =>
      Math.round(
        (breakdown.reduce(
          (sum, b) =>
            (b.tax_category ?? "taxable") === "taxable" &&
            typeof b.amount === "number"
              ? sum + b.amount
              : sum,
          0,
        ) *
          BREAKDOWN_TAX_RATE) /
          100,
      ),
    [breakdown],
  );

  const clearAiFlag = (name: string) => {
    setAiFilledFields((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  // フォームの category フィールドには BudgetCategory.category_id を保持する。
  // BE 送信時に id → name を引いて legacy `category` フィールドにも詰める。

  // 支出カテゴリマスタ Single Source of Truth = BudgetCategory
  // ユーザーが追加した支出明細項目(expense_group 付き)を選べる。
  const { items: budgetCategories, getById: getBudgetCategoryById } =
    useExpenseBudgetCategories();

  // カテゴリ(上段セレクト)→明細項目(下段セレクト) の2段。グレー枠で1セットとして表示。
  const [selectedCat, setSelectedCat] = useState("");

  // フォーム上で追加した「保存前の明細項目 (と必要なら新カテゴリ)」を保持する。
  // ここでは API を叩かず、支出の登録/更新 (onSubmit) 時にまとめて作成する
  // (支出を保存せずに科目だけ先に作成/PATCH されるのを防ぐ)。
  const [pendingItems, setPendingItems] = useState<
    { tempId: string; name: string; expenseGroup: string }[]
  >([]);

  // 下段: 明細項目。選択中カテゴリ(expense_group)配下のみ。並びは display_order。
  const detailOptions = useMemo(() => {
    const persisted = [...budgetCategories]
      // 親カテゴリ(level="category")は明細科目として選ばせない。子(item)のみ。
      .filter(
        (c) =>
          (c.expense_group ?? "") === selectedCat &&
          (c as any).level !== "category",
      )
      .sort((a, b) => (a.display_order ?? 9999) - (b.display_order ?? 9999))
      .map((c) => ({ value: c.category_id, label: c.name }));
    // ローカル追加した明細項目 (support の保存時にまとめて作成される)。
    const pending = pendingItems
      .filter((p) => p.expenseGroup === selectedCat)
      .map((p) => ({ value: p.tempId, label: p.name }));
    return [...persisted, ...pending];
  }, [budgetCategories, selectedCat, pendingItems]);

  // 明細項目(category)が外部(編集ロード/OCR/モーダル作成)で設定されたら、その
  // expense_group を上段カテゴリへ反映。ユーザーがカテゴリを変えた時は category="" に
  // なるのでここは何もしない (上段の選択を保持)。
  const watchedCategoryForGroup = watch("category");
  useEffect(() => {
    if (!watchedCategoryForGroup) return;
    // ローカル追加の明細項目は pending から expense_group を引く。
    const pending = pendingItems.find(
      (p) => p.tempId === watchedCategoryForGroup,
    );
    const g = pending
      ? pending.expenseGroup
      : getBudgetCategoryById(watchedCategoryForGroup)?.expense_group;
    if (g) setSelectedCat(g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedCategoryForGroup, budgetCategories, pendingItems]);

  // 追加モーダル用のカテゴリ(expense_group)選択肢。ユーザー管理のカテゴリのみ。
  const expenseGroupOptions = useMemo(() => {
    const groups = Array.from(
      new Set(
        budgetCategories
          .map((c) => c.expense_group)
          .filter((g): g is string => !!g),
      ),
    );
    return groups.map((g) => ({
      value: g,
      label: g,
    }));
  }, [budgetCategories]);

  // 明細項目追加モーダル (カテゴリ + 明細項目名)。収入の onAddCategory と同型。
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [addCatGroup, setAddCatGroup] = useState("");
  const [addCatName, setAddCatName] = useState("");
  const submitAddDetailCategory = () => {
    const name = addCatName.trim();
    const group = addCatGroup.trim();
    if (!name || !group) return;
    // 既存の明細科目 or 追加済み pending と重複していないか。
    const dupPersisted = budgetCategories.some(
      (c: any) =>
        !c.deleted_at &&
        (c.expense_group ?? "") === group &&
        (c as any).level !== "category" &&
        c.name === name,
    );
    const dupPending = pendingItems.some(
      (p) => p.expenseGroup === group && p.name === name,
    );
    if (dupPersisted || dupPending) {
      showToast({
        message: `明細項目「${name}」は既に存在します`,
        type: "error",
      });
      return;
    }
    // ローカルのみ: 一時 ID で保持し、明細セレクトに選択表示する。
    const tempId = `__new__${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    setPendingItems((prev) => [...prev, { tempId, name, expenseGroup: group }]);
    setSelectedCat(group);
    setValue("category", tempId, { shouldDirty: true, shouldValidate: true });
    setAddCatOpen(false);
    setAddCatName("");
    setAddCatGroup("");
  };

  // OCR で AI が埋めた field をユーザーが手動編集 → AI フラグを外す
  useEffect(() => {
    if (Object.keys(aiFilledFields).length === 0) return;
    const sub = watch((_v, info) => {
      if (info?.name && aiFilledFields[info.name]) {
        clearAiFlag(info.name);
      }
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiFilledFields]);

  // ── 場所区分 → 名称 (Unit) 連動 ──
  // RepairWorkCreateModal と同じパターン:
  //   target_type を変えると Unit を fetch して名称セレクトに流し込み、
  //   ユーザーが target_type を変えた時だけ target_id をクリアする (reset() では消さない)。
  const targetTypeValue = watch("target_type");
  // ステータス (sticky タイトル右上のセレクトで表示・変更)。
  const statusValue = watch("status");
  // 工事費カテゴリ (system seed: cat_system_opex) のときだけ「工事名」プルダウンを表示するためのフラグ
  const watchedCategoryId = watch("category");
  const isRepairCategorySelected = watchedCategoryId === "cat_system_opex";
  const [areaOptions, setAreaOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);

  // ユーザー操作で target_type を変えた時のみ target_id をクリア
  useEffect(() => {
    const sub = watch((_v, info) => {
      if (info?.type === "change" && info?.name === "target_type") {
        setValue("target_id", "");
      }
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // target_type / propertyId 変更で Unit 一覧を取得 (area_type で絞り込み)
  useEffect(() => {
    if (!propertyId || !targetTypeValue) {
      setAreaOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoadingAreas(true);
      try {
        const res = await ipponApi.get("/api/v1/units", {
          params: { property_id: propertyId, area_type: targetTypeValue },
        });
        const items = res.data?.data?.items;
        const units = Array.isArray(items) ? items : [];
        if (cancelled) return;
        setAreaOptions(
          units.map((u: any) => ({
            label: String(u.landmark_name || ""),
            value: String(u.unit_id || ""),
          })),
        );
      } catch {
        if (!cancelled) setAreaOptions([]);
      } finally {
        if (!cancelled) setIsLoadingAreas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId, targetTypeValue]);

  // 修繕に紐付け用: 物件配下の修繕一覧
  const [repairOptions, setRepairOptions] = useState<
    { label: string; value: string }[]
  >([]);
  useEffect(() => {
    if (!propertyId) {
      setRepairOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await ipponApi.get("/api/v1/repairs", {
          params: { property_id: propertyId, limit: 500 },
        });
        const items = res.data?.data?.items || res.data?.data?.repairs || [];
        const arr = Array.isArray(items) ? items : [];
        if (cancelled) return;
        setRepairOptions(
          arr.map((r: any) => ({
            label: String(r.repair_title || r.repair_id || ""),
            value: String(r.repair_id || ""),
          })),
        );
      } catch {
        if (!cancelled) setRepairOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  // 編集モードで preset id 解決を行うのは fetchExpenseById の reset で完了する。
  // (後方互換 name 一致 fallback は削除済み)

  const fetchDetail = useCallback(async () => {
    setInitialLoadError(null);
    if (isNew) return;
    setLoading(true);
    try {
      const rec = await dispatch(
        fetchExpenseById({ expense_id: expenseId }),
      ).unwrap();
      setData(rec);
      setPropertyId(rec.property_id);
      onLoadedRef.current?.(rec);
      // budget_category_id が真のカテゴリ参照。未設定の旧レコードは
      // フォーム上空文字 → 必須バリデーションで再選択させる。
      reset({
        name: rec.name,
        category: (rec as any).budget_category_id || "",
        expense_type: rec.expense_type,
        amount: String(rec.amount ?? ""),
        budget:
          (rec as any).budget !== undefined && (rec as any).budget !== null
            ? String((rec as any).budget)
            : "",
        date: rec.date,
        status: rec.status,
        description: rec.description || "",
        target_type: rec.target_type || "",
        target_id: rec.target_unit_id || "",
        linked_repair_id: (rec as any).linked_repair_id || "",
        documents: ((rec.documents || []) as any).map(
          (d: any) => d.s3_key || d.url || d,
        ),
        payee: (rec as any).payee || "",
        partner_name: (rec as any).partner_name || "",
        tax_rate:
          (rec as any).tax_rate !== undefined && (rec as any).tax_rate !== null
            ? String((rec as any).tax_rate)
            : "",
        tax_amount:
          (rec as any).tax_amount !== undefined &&
          (rec as any).tax_amount !== null
            ? String((rec as any).tax_amount)
            : "",
        registration_number: (rec as any).registration_number || "",
      });
      // 内訳もロード (編集モード)。明細が無い旧データは
      // amount(税込) − tax_amount から「一式」1 行を合成し、合計を保持する。
      const recBreakdown = (rec as any).breakdown as
        | ExpenseBreakdownItem[]
        | undefined;
      const loaded =
        Array.isArray(recBreakdown) && recBreakdown.length > 0
          ? recBreakdown.map((b) => ({
              item_id: b.item_id,
              item: b.item || "",
              amount: typeof b.amount === "number" ? b.amount : 0,
              // 税区分が無い旧データは課税扱い
              tax_category: b.tax_category ?? "taxable",
            }))
          : buildInitialBreakdown({
              fallbackAmount: rec.amount ?? undefined,
              fallbackTax: (rec as any).tax_amount ?? undefined,
            });
      setBreakdown(loaded);
      // クリーン状態の明細スナップショットを更新 (以降はこの内容と比較して活性判定)。
      initialBreakdownSerializedRef.current = serializeBreakdown(loaded);
      // カスタムフォーム
      const loadedCustomFields = (((rec as any).custom_fields as any) ||
        []) as ExpenseCustomFieldValue[];
      setCustomFieldValues(loadedCustomFields);
      // ロード時は未保存の pending fields を破棄 (前 record の入力を引き継がない)
      setPendingCustomFields([]);
      initialCustomFieldsSerializedRef.current = serializeExpenseCustomFields(
        loadedCustomFields,
        [],
      );
      // ロード直後は dirty ではない
      setLocalDirty(false);
    } catch (err) {
      const payload = err as ApiErrorPayload | undefined;
      const errorType = payload?.errorType ?? "unknown";
      setInitialLoadError(errorType);
      if (errorType === "notFound" || !dataRef.current) {
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, expenseId, isNew, reset]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (!propertyId || breadcrumbPropertyName || embedded) return;
    let cancelled = false;
    const fetchPropertyName = async () => {
      try {
        const BaseURL = import.meta.env.VITE_API_URL;
        const response = await ipponApi.get(
          `${BaseURL}/api/v1/properties/${propertyId}`,
        );
        const name = response?.data?.data?.name;
        if (!cancelled && typeof name === "string") {
          setBreadcrumbPropertyName(name);
        }
      } catch {
        // 物件名が取得できない場合も支出詳細自体は表示を継続する。
      }
    };
    fetchPropertyName();
    return () => {
      cancelled = true;
    };
  }, [breadcrumbPropertyName, embedded, propertyId]);

  // AI チャットアクション完了時 (refetchBus.emit('expense:<id>')) に再フェッチ
  useRefetchOn("expense", isNew ? undefined : expenseId, fetchDetail);

  // パンくず最終項目に支出タイトル (= data.name) を反映するため、
  // navigation state に title を埋め込む (= MainLayout の breadcrumb builder が
  //   location.state.title を customerName として拾う)。
  // 埋め込み/新規モードでは履歴汚染を避けてスキップ。
  const lastBreadcrumbStateRef = useRef<string>("");
  useEffect(() => {
    if (isNew || embedded) return;
    if (!data?.name) return;
    const resolvedPropertyId = data.property_id || propertyId;
    const stateKey = `${data.name}|${resolvedPropertyId}|${breadcrumbPropertyName}`;
    if (stateKey === lastBreadcrumbStateRef.current) return;
    lastBreadcrumbStateRef.current = stateKey;
    const currentState = (location.state as Record<string, unknown>) || {};
    const nextSearchParams = new URLSearchParams(location.search);
    if (resolvedPropertyId) {
      nextSearchParams.set("property_id", resolvedPropertyId);
    }
    const search = nextSearchParams.toString();
    navigate(`/expenses/${expenseId}${search ? `?${search}` : ""}`, {
      state: {
        ...currentState,
        title: data.name,
        propName: breadcrumbPropertyName || currentState.propName,
        propertyId: resolvedPropertyId,
      },
      replace: true,
    });
  }, [
    data?.name,
    data?.property_id,
    expenseId,
    isNew,
    embedded,
    navigate,
    location.state,
    location.search,
    propertyId,
    breadcrumbPropertyName,
  ]);

  const scrollToFirstError = useScrollToFirstError<FormValues>();
  const onSubmit = handleSubmit(async (values) => {
    // 金額の桁数オーバー (12桁以上) は ExpenseBreakdownTable が赤字表示。送信を止めて
    // 該当の内訳テーブルへスクロールする。
    if (breakdown.some((b) => isBillingAmountTooLong(b.amount))) {
      requestAnimationFrame(() => {
        breakdownRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
      return;
    }
    const amountRequiredErrorItemIds =
      getBreakdownAmountRequiredErrorItemIds(breakdown);
    setBreakdownAmountRequiredErrorItemIds(amountRequiredErrorItemIds);
    if (amountRequiredErrorItemIds.size > 0) {
      requestAnimationFrame(() => {
        breakdownRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
      return;
    }
    setSubmitting(true);
    try {
      // values.category には BudgetCategory.category_id が入る (必須)。
      // BE 送信時は category_id → name を引いて legacy `category` 文字列にも詰める。
      let submitCategoryName: string;
      let submitBudgetCategoryId: string;
      const pending = pendingItems.find((p) => p.tempId === values.category);
      if (pending) {
        // ローカル追加の明細項目を、支出保存の直前にまとめて作成する。
        // 親カテゴリ(level="category")を解決。無ければカテゴリ(親)も新規作成してから、
        // 明細科目(子)を作る。→ 支出を保存しない限り科目は作られない。
        let parentId = budgetCategories.find(
          (c: any) =>
            !c.deleted_at &&
            c.level === "category" &&
            (c.expense_group ?? "") === pending.expenseGroup,
        )?.category_id as string | undefined;
        if (!parentId) {
          const createdCat: any = await dispatch(
            createBudgetCategory({
              name: pending.expenseGroup,
              category_type: "expense_opex",
              level: "category",
              expense_group: pending.expenseGroup,
            } as any),
          ).unwrap();
          parentId = createdCat.category_id;
        }
        const createdItem: any = await dispatch(
          createBudgetCategory({
            name: pending.name,
            category_type: "expense_opex",
            level: "item",
            parent_category_id: parentId,
          } as any),
        ).unwrap();
        submitBudgetCategoryId = createdItem.category_id;
        submitCategoryName = createdItem.name;
        await dispatch(fetchBudgetCategories());
        // 実 ID を確定させ、pending から除去 (再送信で二重作成しない)。
        setValue("category", createdItem.category_id, {
          shouldValidate: false,
        });
        setPendingItems((prev) =>
          prev.filter((p) => p.tempId !== pending.tempId),
        );
      } else {
        const selectedCat = getBudgetCategoryById(values.category);
        if (!selectedCat) {
          showToast({
            type: "error",
            message: "カテゴリを選択してください",
          });
          return;
        }
        submitCategoryName = selectedCat.name;
        submitBudgetCategoryId = selectedCat.category_id;
      }
      // 空行 (item 空 + amount 0) は BE に送らない。税区分は各行が保持。
      const cleanBreakdown = breakdown
        .filter((b) => (b.item || "").trim() !== "" || b.amount !== 0)
        .map((b) => ({ ...b, tax_category: b.tax_category ?? "taxable" }));
      // 支払金額/税率/消費税の手入力は廃止。明細(税抜)合計 + 消費税(課税行×10%) を
      // 支払総額(税込)として送る。税区分は明細の各行が持つ。
      const submitSubtotal = cleanBreakdown.reduce(
        (sum, b) => sum + (typeof b.amount === "number" ? b.amount : 0),
        0,
      );
      const submitTaxAmount = Math.round(
        (cleanBreakdown.reduce(
          (sum, b) =>
            (b.tax_category ?? "taxable") === "taxable" &&
            typeof b.amount === "number"
              ? sum + b.amount
              : sum,
          0,
        ) *
          BREAKDOWN_TAX_RATE) /
          100,
      );
      // 課税行が 1 つでもあれば標準税率を、無ければ 0 を記録する。
      const submitTaxRate = submitTaxAmount > 0 ? BREAKDOWN_TAX_RATE : 0;
      const submitAmount = submitSubtotal + submitTaxAmount;
      // 明細が 1 件も無い (合計が算出できない) 場合は登録不可。
      // トーストではなく明細テーブル直下にインライン表示し、該当箇所へスクロール。
      if (submitAmount <= 0) {
        setBreakdownEmptyError(true);
        breakdownRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
      // pending fields の flush: master upsert → 戻りの field_id を custom_fields にマージ。
      // 失敗時は record save も実行しない。
      let mergedCustomFields = customFieldValues;
      if (pendingCustomFields.length > 0) {
        const results = await Promise.all(
          pendingCustomFields.map((p) =>
            dispatch(
              createExpenseCustomField({
                label: p.label,
                field_type: "text",
                applies_to_expense_types: values.expense_type
                  ? [values.expense_type as "OPEX" | "CAPEX"]
                  : undefined,
              }),
            ),
          ),
        );
        const failed = results.find(
          (r) => !createExpenseCustomField.fulfilled.match(r),
        );
        if (failed) {
          const msg =
            (failed.payload as string) ||
            "カスタムフォーム項目の追加に失敗しました";
          showToast({ type: "error", message: msg });
          return;
        }
        const newValues: ExpenseCustomFieldValue[] = results.map((r, i) => ({
          field_id: (r.payload as { field_id: string }).field_id,
          value: pendingCustomFields[i].value,
        }));
        mergedCustomFields = [...mergedCustomFields, ...newValues];
        setPendingCustomFields([]);
      }
      if (isNew) {
        if (!propertyId) {
          showToast({
            type: "error",
            message: "物件情報が必要です (?property_id=xxx で指定してください)",
          });
          return;
        }
        const action: any = await dispatch(
          createExpense({
            property_id: propertyId,
            name: values.name,
            category: submitCategoryName,
            expense_type: values.expense_type,
            // 支払総額(税込) = 明細合計(税抜) + 消費税額 (自動算出)
            amount: submitAmount,
            budget:
              values.budget !== undefined && values.budget !== ""
                ? Number(values.budget)
                : undefined,
            date: values.date,
            status: values.status,
            description: values.description,
            target_type: (values.target_type || undefined) as any,
            target_unit_id: values.target_id || undefined,
            target_name_snapshot:
              (values.target_id &&
                areaOptions.find((o) => o.value === values.target_id)?.label) ||
              undefined,
            source: "direct",
            linked_repair_id: values.linked_repair_id || undefined,
            // 「その他関連書類」(documents): ユーザーが UI で追加したファイルのみ
            documents: (values.documents ?? []) as any,
            // 「AI 抽出対象書類」: 登録時の OCR 取込ドキュメントを ai_documents として保存
            ai_documents: presetOcrDocument
              ? [
                  {
                    document_id: presetOcrDocument.document_id,
                    file_name: presetOcrDocument.file_name,
                    file_type: presetOcrDocument.file_type,
                    s3_key: presetOcrDocument.s3_key,
                    uploaded_at: new Date().toISOString(),
                  } as any,
                ]
              : undefined,
            // 予実連携: 常に id を送る (Single Source of Truth = BudgetCategory)
            budget_category_id: submitBudgetCategoryId,
            // 帳簿要件。税率/消費税は明細の税区分から自動算出した値を送る。
            payee: values.payee || undefined,
            partner_name: values.partner_name || undefined,
            tax_rate: submitTaxRate,
            tax_amount: submitTaxAmount,
            registration_number: values.registration_number || undefined,
            breakdown: cleanBreakdown.length > 0 ? cleanBreakdown : undefined,
            custom_fields:
              mergedCustomFields.length > 0 ? mergedCustomFields : undefined,
            // OCR メタ
            ocr_source_document_id: presetOcrDocument?.document_id,
            ocr_extracted_at: presetOcrDocument
              ? new Date().toISOString()
              : undefined,
            ai_filled_fields:
              presetAiFilledFields && presetAiFilledFields.length > 0
                ? presetAiFilledFields
                : undefined,
          } as any),
        );
        const created = action?.payload;
        if (created?.expense_id) {
          showToast({ type: "success", message: "支出を登録しました" });
          onCreated?.(created.expense_id);
          if (!embedded) {
            const search = propertyId
              ? `?property_id=${encodeURIComponent(propertyId)}`
              : "";
            navigate(`/expenses/${created.expense_id}${search}`, {
              state: {
                propName:
                  breadcrumbPropertyName ||
                  (location.state as Record<string, unknown> | null)?.propName,
                propertyId,
              },
            });
          }
        } else {
          showToast({ type: "error", message: "支出の登録に失敗しました" });
        }
      } else {
        const action: any = await dispatch(
          updateExpense({
            expense_id: expenseId,
            body: {
              name: values.name,
              category: submitCategoryName,
              expense_type: values.expense_type,
              // 支払総額(税込) = 明細合計(税抜) + 消費税額 (自動算出)
              amount: submitAmount,
              budget:
                values.budget !== undefined && values.budget !== ""
                  ? Number(values.budget)
                  : null,
              date: values.date,
              status: values.status as ExpenseRecord["status"],
              description: values.description,
              target_type: (values.target_type || undefined) as any,
              target_unit_id: values.target_id || undefined,
              target_name_snapshot:
                (values.target_id &&
                  areaOptions.find((o) => o.value === values.target_id)
                    ?.label) ||
                undefined,
              linked_repair_id: values.linked_repair_id || undefined,
              budget_category_id: submitBudgetCategoryId,
              // 帳簿要件。税率/消費税は明細の税区分から自動算出した値を送る。
              payee: values.payee || undefined,
              partner_name: values.partner_name || undefined,
              tax_rate: submitTaxRate,
              tax_amount: submitTaxAmount,
              registration_number: values.registration_number || undefined,
              breakdown: cleanBreakdown,
              custom_fields: mergedCustomFields,
            } as any,
          }),
        );
        if (action?.type?.endsWith("/fulfilled")) {
          showToast({ type: "success", message: "支出を更新しました" });
          // 編集後にフォームの dirty 状態を解除
          reset(values);
          setLocalDirty(false);
          // 保存成功後は現在の明細をクリーン状態として記録 (以降の比較基準)。
          initialBreakdownSerializedRef.current = serializeBreakdown(breakdown);
          initialCustomFieldsSerializedRef.current =
            serializeExpenseCustomFields(mergedCustomFields, []);
          onUpdated?.(expenseId);
        } else {
          showToast({ type: "error", message: "支出の更新に失敗しました" });
        }
      }
    } catch {
      showToast({
        type: "error",
        message: isNew
          ? "支出の登録に失敗しました"
          : "支出の更新に失敗しました",
      });
    } finally {
      setSubmitting(false);
    }
  }, scrollToFirstError);

  const hasExpenseData = Boolean(data);
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasExpenseData,
    dedupeKey: `expense:${expenseId}`,
  });

  if (shouldShowInitialLoadError(initialLoadError, hasExpenseData)) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchDetail} />
    );
  }

  if (loading || (!isNew && !data)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          minHeight: "60vh",
          width: "100%",
        }}
      >
        <CustomLoading loading={true} fullScreen={false} size="medium" />
      </Box>
    );
  }

  const isModalEmbed = !!embedded && isNew;
  // 全モード (フルページ / 登録モーダル / ドロワー) で 600px 固定。
  const effectiveInputWidth = INPUT_WIDTH;

  const aiFilledCount = Object.keys(aiFilledFields).length;

  /**
   * AI で自動入力された field の labelSuffix に AiBadge を返す。
   * 各 input の `labelSuffix` prop に流すと、ラベル文字列 (+必須*) の直後に並ぶ。
   * フラグが解除されたら undefined を返して chip を消す。
   */
  const aiSuffix = (name: string): React.ReactNode =>
    aiFilledFields[name] ? <AiBadge /> : undefined;

  const formInner = (
    <>
      {/* OCR サマリバナー */}
      {(aiFilledCount > 0 ||
        (presetOcrWarnings && presetOcrWarnings.length > 0)) && (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {aiFilledCount > 0 && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <AiBadge label={`${aiFilledCount}件をAIが入力`} />
              <Typography sx={{ fontSize: 12, color: colors.text.secondary }}>
                AI入力項目を確認し、必要に応じて修正してください
              </Typography>
            </Stack>
          )}
          {presetOcrWarnings?.map((w, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{
                p: 1.25,
                borderRadius: "8px",
                border: `1px solid ${colors.status.warning}`,
                backgroundColor: "rgba(255, 152, 0, 0.08)",
              }}
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 16, color: colors.status.warning, mt: "2px" }}
              />
              <Typography sx={{ fontSize: 12, color: colors.text.primary }}>
                {w}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
      {/* === 概要情報 === */}
      <CustomSectionTitle title="概要情報" size="small" />
      <Box sx={{ display: "flex", flexDirection: "column", mt: 2, gap: 0 }}>
        {/* AI 抽出対象書類: read-only プレビュー (OCR 取込時 or 既存 ai_documents 時のみ表示) */}
        {(() => {
          const aiInitial: Array<{
            data: string;
            originalFileName: string;
            mimeType: string;
            isUrl: true;
          }> = data?.ai_documents
            ? (data.ai_documents as any[]).map((d: any) => ({
                data: d.s3_key,
                originalFileName: d.file_name || "",
                mimeType: d.file_type || "",
                isUrl: true as const,
              }))
            : presetOcrDocument
              ? [
                  {
                    data: presetOcrDocument.signed_url,
                    originalFileName: presetOcrDocument.file_name,
                    mimeType: presetOcrDocument.file_type,
                    isUrl: true as const,
                  },
                ]
              : [];
          if (aiInitial.length === 0) return null;
          return (
            <Box sx={{ mb: 2, maxWidth: 451 }}>
              <CustomFileUploader
                label="AI抽出対象書類"
                name="ai_documents_readonly"
                setValue={setValue as any}
                register={register as any}
                initialImages={aiInitial as any}
                width="180px"
                isReadOnly
              />
            </Box>
          );
        })()}

        <CustomCalendarInput
          label="発生日"
          name="date"
          control={control}
          isRequired
          labelWidth={LABEL_WIDTH}
          labelWidthSp="47%"
          inputWidth={effectiveInputWidth}
          inputWidthSp="100%"
        />

        <CustomTextInput
          label="支出タイトル"
          name="name"
          placeholder="例: 電気代4月分、外壁塗装工事"
          register={register}
          errors={errors}
          labelWidth={LABEL_WIDTH}
          labelWidthSp="47%"
          inputWidth={effectiveInputWidth}
          inputWidthSp="100%"
          isRequired
          labelSuffix={aiSuffix("name")}
        />

        {isModalEmbed && (
          <CustomCategorySelectSingle
            label="ステータス"
            name="status"
            clearable={false}
            searchable={false}
            options={STATUS_OPTIONS}
            selectedValue={statusValue || ""}
            isRequired
            onValueChange={(val) => {
              if (!val || !val.trim()) return;
              setValue("status", val, {
                shouldDirty: true,
                shouldTouch: true,
              });
            }}
            labelWidth={LABEL_WIDTH}
            inputWidth={effectiveInputWidth}
            fontSize={12}
            placeholder="ステータス"
          />
        )}

        <CustomSingleSelect
          label="場所区分"
          name="target_type"
          control={control}
          errors={errors}
          options={TARGET_TYPE_OPTIONS}
          labelWidth={LABEL_WIDTH}
          labelWidthSp="47%"
          inputWidth={effectiveInputWidth}
          inputWidthSp="100%"
        />
        {/* 場所区分の下に紐付け Unit (名称) を配置 */}
        <Collapse in={!!targetTypeValue} timeout={400}>
          <Box
            sx={{
              ml: 3,
              position: "relative",
              transition: "all 0.4s ease-out",
              transform: targetTypeValue
                ? "translateX(0)"
                : "translateX(-20px)",
              opacity: targetTypeValue ? 1 : 0,
            }}
          >
            <SubdirectoryArrowRightIcon
              sx={{
                position: "absolute",
                left: -20,
                top: 0,
                fontSize: 16,
                color: "#989898",
                pointerEvents: "none",
              }}
            />
            <CustomSingleSelect
              label="名称"
              name="target_id"
              control={control}
              errors={errors}
              placeholder={
                isLoadingAreas
                  ? "読み込み中..."
                  : areaOptions.length === 0
                    ? "該当する区画がありません"
                    : "選択してください"
              }
              options={areaOptions}
              labelWidth={LABEL_WIDTH}
              labelWidthSp="47%"
              inputWidth={`calc(${effectiveInputWidth} - 24px)`}
              inputWidthSp="100%"
              disabled={isLoadingAreas || areaOptions.length === 0}
            />
          </Box>
        </Collapse>

        {/* カテゴリ → 明細項目 の2段セレクト。1セットとしてグレー枠で囲う
            (収入の「場所・テナント選択」と同じ位置づけ)。 */}
        <Box
          sx={{
            border: `1px solid ${colors.border.light}`,
            borderRadius: "8px",
            backgroundColor: colors.background.light,
            // グレー枠は 600px (= INPUT_WIDTH) 幅。padding/border 込みで 600px に収める。
            width: "100%",
            maxWidth: INPUT_WIDTH,
            boxSizing: "border-box",
            // 枠と input の隙間、および input 同士の隙間を 8px に。
            p: 1,
            mb: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          {/* 上段: カテゴリ。選ぶと明細項目を絞る。 */}
          <CustomCategorySelectSingle
            label="カテゴリ"
            name="_expense_group"
            isRequired
            labelWidth={LABEL_WIDTH}
            inputWidth="100%"
            options={expenseGroupOptions}
            selectedValue={selectedCat}
            onValueChange={(v) => {
              setSelectedCat(String(v));
              // カテゴリを変えたら明細項目をクリア (別カテゴリの選択を持ち越さない)
              setValue("category", "", {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            placeholder="選択してください"
          />
          {/* 下段: 明細項目。選択中カテゴリ配下。「追加」モーダルで追加。 */}
          <CustomCategorySelectSingle
            label="明細項目"
            name="category"
            labelSuffix={aiSuffix("category")}
            isRequired
            labelWidth={LABEL_WIDTH}
            inputWidth="100%"
            errors={
              errors.category
                ? { category: { message: errors.category.message } }
                : {}
            }
            options={detailOptions}
            selectedValue={watchedCategoryForGroup || ""}
            onValueChange={(v) =>
              setValue("category", String(v), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onAddNew={() => {
              setAddCatGroup(selectedCat || "");
              setAddCatName("");
              setAddCatOpen(true);
            }}
            placeholder={
              selectedCat ? "選択してください" : "先にカテゴリを選択"
            }
          />
          {/* カテゴリ=工事費 (system: cat_system_opex) の時のみ工事名 (修繕紐付け)。
              カテゴリ/明細項目と同じグレー枠内に、明細項目の下位として収める。 */}
          <Collapse in={isRepairCategorySelected} timeout={400}>
            <Box
              sx={{
                ml: 3,
                position: "relative",
                transition: "all 0.4s ease-out",
              }}
            >
              <SubdirectoryArrowRightIcon
                sx={{
                  position: "absolute",
                  left: -20,
                  top: 0,
                  fontSize: 16,
                  color: "#989898",
                  pointerEvents: "none",
                }}
              />
              <CustomSingleSelect
                label="工事名"
                name="linked_repair_id"
                control={control}
                errors={errors}
                placeholder={
                  repairOptions.length === 0
                    ? "対象工事がありません"
                    : "選択してください"
                }
                options={repairOptions}
                labelWidth={LABEL_WIDTH}
                labelWidthSp="47%"
                inputWidth="100%"
                inputWidthSp="100%"
                disabled={repairOptions.length === 0}
              />
            </Box>
          </Collapse>
        </Box>
        {/* RHF に明細項目値を登録して必須バリデーションを有効化 */}
        <input
          type="hidden"
          {...register("category", {
            required: "明細項目は必須です",
          })}
        />

        {/* ステータスは収入詳細と同様、sticky タイトル行の右上へ移設 (下のヘッダー参照)。 */}

        <CustomSingleSelect
          label="経費区分"
          name="expense_type"
          control={control}
          errors={errors}
          options={EXPENSE_TYPE_OPTIONS}
          labelWidth={LABEL_WIDTH}
          labelWidthSp="47%"
          inputWidth={effectiveInputWidth}
          inputWidthSp="100%"
          isRequired
          labelSuffix={aiSuffix("expense_type")}
        />

        <CustomTextInput
          label="予算"
          name="budget"
          register={register}
          errors={errors}
          type="number"
          showYen
          watch={watch}
          setValue={setValue}
          labelWidth={LABEL_WIDTH}
          labelWidthSp="47%"
          inputWidth={effectiveInputWidth}
          inputWidthSp="100%"
        />
        {/* 支払金額の手入力は廃止。合計は「取引明細」セクションの金額サマリーで自動算出する。 */}
      </Box>

      {/* === 取引明細 === */}
      <Box mt={2}>
        <CustomSectionTitle title="取引明細" size="small" />
        {/* 明細テーブル + 合計のみ広げる (基本情報フィールドは 600px のまま)。
            収入側 (BillingItemsTable) と同様に固定幅 860px。 */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: { xs: "100%", md: 860 },
            maxWidth: "100%",
          }}
        >
          {/* 内訳: 表なのでフォームより上下余白を詰める。各行に税区分 (課税/非課税) を持つ。 */}
          <Box ref={breakdownRef} sx={{ width: "100%", mb: 2 }}>
            <ExpenseBreakdownTable
              items={breakdown}
              onChange={(next) => {
                setBreakdown(next);
                setBreakdownEmptyError(false); // 編集したらインライン必須エラーを解除
                setBreakdownAmountRequiredErrorItemIds((prev) =>
                  prev.size > 0
                    ? getBreakdownAmountRequiredErrorItemIds(next)
                    : prev,
                );
                // 明細の dirty は breakdownDirty(スナップショット比較)で判定するため
                // ここで localDirty は立てない (元に戻したら非活性へ戻すため)。
              }}
              aiFilledItemIds={aiBreakdownIds}
              amountRequiredErrorItemIds={breakdownAmountRequiredErrorItemIds}
            />
            {breakdownEmptyError && (
              <Typography color="error" sx={{ fontSize: 12, mt: 0.5 }}>
                金額を1件以上入力する必要があります
              </Typography>
            )}
          </Box>

          {/* 金額サマリー (収入詳細と同じ構成: 小計 / 消費税 / 合計)。
              支払金額・税率・消費税の手入力は廃止。税区分は明細の各行で指定し、
              消費税 = Σ(課税行 × 10%)、合計(税込) = 小計 + 消費税 を表示専用で自動算出する。 */}
          <Box sx={{ width: "100%" }}>
            <ExpenseAmountSummary subtotal={subtotal} taxAmount={taxAmount} />
          </Box>
        </Box>
      </Box>

      {/* === 支払先情報 === */}
      <Box mt={2}>
        <CustomSectionTitle title="取引先情報" size="small" />
        <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
          <CustomTextInput
            label="支払先"
            name="payee"
            placeholder="支払先の名称"
            register={register}
            errors={errors}
            labelWidth={LABEL_WIDTH}
            labelWidthSp="47%"
            inputWidth={effectiveInputWidth}
            inputWidthSp="100%"
            labelSuffix={aiSuffix("payee")}
          />
          <CustomTextInput
            label="取引先名"
            name="partner_name"
            placeholder="取引先 (仕入先・発行者) の名称"
            register={register}
            errors={errors}
            labelWidth={LABEL_WIDTH}
            labelWidthSp="47%"
            inputWidth={effectiveInputWidth}
            inputWidthSp="100%"
            labelSuffix={aiSuffix("partner_name" as any)}
          />
          <CustomTextInput
            label="登録番号"
            name="registration_number"
            placeholder="T+13桁（例: T1234567890123）"
            register={register}
            errors={errors}
            labelWidth={LABEL_WIDTH}
            labelWidthSp="47%"
            inputWidth={effectiveInputWidth}
            inputWidthSp="100%"
            labelSuffix={aiSuffix("registration_number")}
          />
        </Box>
      </Box>

      {/* === その他 (カスタム項目 / 備考 / 関連書類) === */}
      <Box mt={0}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* カスタムフォーム (セクションタイトルなし、現状仕様維持) */}
          <Box sx={{ maxWidth: effectiveInputWidth }}>
            <ExpenseCustomFieldsAddable
              expenseType={watch("expense_type") as "OPEX" | "CAPEX"}
              values={customFieldValues}
              onChange={(next) => {
                setCustomFieldValues(next);
              }}
              pendingFields={pendingCustomFields}
              onPendingChange={(next) => {
                setPendingCustomFields(next);
              }}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <CustomTextInput
              label="備考"
              name="description"
              placeholder="詳細な説明やメモを入力"
              register={register}
              errors={errors}
              multiline
              rows={3}
              labelWidth={LABEL_WIDTH}
              labelWidthSp="47%"
              inputWidth={effectiveInputWidth}
              inputWidthSp="100%"
            />
          </Box>

          {/* その他関連書類: ユーザー添付 (追加・削除・プレビュー可) */}
          <Box sx={{ maxWidth: 451 }}>
            <CustomFileUploader
              label="その他関連書類"
              name="documents"
              setValue={setValue as any}
              register={register as any}
              initialImages={
                (data?.documents
                  ? (data.documents as any[]).map((d: any) => ({
                      data: d.s3_key,
                      originalFileName: d.file_name || "",
                      mimeType: d.file_type || "",
                      isUrl: true,
                    }))
                  : []) as any
              }
              onImageChange={() => setLocalDirty(true)}
              width="180px"
            />
          </Box>
        </Box>
      </Box>
    </>
  );

  const addDetailCategoryModal = (
    <CustomModal
      title="明細項目の追加"
      openModal={addCatOpen}
      handleCloseModal={() => setAddCatOpen(false)}
      bodyText=""
      hideCloseButton
      modalType="deleteModal"
    >
      <Box sx={{ mt: 1, minWidth: 300 }}>
        <Box sx={{ mb: 1.5 }}>
          <CustomCategorySelectSingle
            label="カテゴリ"
            name="_add_cat_group"
            options={expenseGroupOptions}
            selectedValue={addCatGroup}
            onValueChange={(v) => setAddCatGroup(String(v))}
            onAddNewOption={(v) => setAddCatGroup(v.trim())}
            isRequired
            labelWidth="88px"
            inputWidth="100%"
            placeholder="選択 / 新しいカテゴリを入力"
          />
        </Box>
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
            明細項目
          </Typography>
          <Box
            component="input"
            autoFocus
            value={addCatName}
            placeholder="明細項目名を入力"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAddCatName(e.target.value)
            }
            sx={{
              width: "100%",
              height: 38,
              px: 1.25,
              fontSize: 13,
              border: `1px solid ${colors.border.medium}`,
              borderRadius: "8px",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            mt: 1,
          }}
        >
          <CustomButton
            label="戻る"
            buttonCategory="cancelModal"
            onClick={() => setAddCatOpen(false)}
          />
          <Can action="expense:edit">
            <CustomButton
              label="追加"
              buttonCategory="submit"
              disabled={!addCatName.trim() || !addCatGroup}
              onClick={submitAddDetailCategory}
              sx={{ width: "50% !important" }}
            />
          </Can>
        </Box>
      </Box>
    </CustomModal>
  );

  if (isModalEmbed) {
    return (
      <>
        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box className="registerModalBody">
            <Box sx={{ maxWidth: "100%" }}>{formInner}</Box>
          </Box>
          <Box className="registerModalFooter">
            <CustomButton
              label="戻る"
              buttonCategory="cancelModal"
              onClick={() => onCancel?.()}
              disabled={submitting}
              sx={{ minWidth: "140px !important", width: "140px" }}
            />
            <Can action="expense:edit">
              <CustomButton
                label="登録"
                type="submit"
                buttonCategory="submit"
                // 新規 OCR フローは defaultValues で値が入った状態で開くので、
                // 明示的なプリセットがあれば dirty 扱いにして登録を有効化する。
                disabled={
                  submitting ||
                  (!hasFormDirty &&
                    !localDirty &&
                    !breakdownDirty &&
                    !customFieldsDirty &&
                    !hasInitialPrefill)
                }
                isLoading={submitting}
                sx={{ minWidth: "140px !important", width: "140px" }}
              />
            </Can>
          </Box>
        </Box>
        {addDetailCategoryModal}
      </>
    );
  }

  // ページ直開き時のみ遷移ガード (埋め込み時はドロワー側で閉じ確認を持つ)。
  return (
    <CustomFormNavigationGuard hasFormChanged={!embedded && combinedDirty}>
      {() => (
        <Box
          pb={9}
          // pt は sticky タイトルの「外」に置くとスクロール時に動くので常に 0。
          // 上余白は sticky タイトル Box の内側 pt で持たせる。
          pt={0}
          // ドロワー時は PreviewDrawer の px(12) + ここ(12) = 24 で全画面(単独ページ)と揃える。
          // 単独ページは 0 (DashboardLayout の pl/pr=24 が効く)。
          px={embedded ? 1.5 : 0}
          data-entity="expense"
          data-id={isNew ? "new" : expenseId || "new"}
        >
          <Box
            sx={{
              mb: 3,
              // タイトルを上端に sticky 固定。ドロワー(embedded)はスクロール先頭(0)、
              // 単独ページ(全画面)は固定ヘッダー(50px)の下に貼り付ける。
              // 上余白は sticky 要素の内側 pt で持たせる (外側だとスクロール時に動く)。
              pt: embedded ? 0 : 0.5,
              position: "sticky",
              top: embedded ? 0 : 50,
              zIndex: 10,
              backgroundColor: "white",
            }}
          >
            <CustomSectionTitle
              title={isNew ? "支出登録" : data?.name || ""}
              size="large"
              addBorder
              icon={!embedded ? <BackButton size={24} /> : undefined}
              rightContent={
                <Box
                  sx={{
                    width: 160,
                    "& .inputLabel": { display: "none" },
                    "& > *": { margin: "0 !important" },
                  }}
                >
                  <CustomCategorySelectSingle
                    label=""
                    name="status"
                    clearable={false}
                    searchable={false}
                    options={STATUS_OPTIONS}
                    selectedValue={statusValue || ""}
                    onValueChange={async (val) => {
                      // 必須項目。空選択 (プレースホルダー) は無視。
                      if (!val || !val.trim()) return;
                      // ステータスだけは収入詳細と同様、「更新」ボタンを待たず即 PATCH する。
                      const previous = statusValue;
                      // 即時保存するのでフォームを dirty にしない (更新ボタンを点けない)。
                      setValue("status", val, {
                        shouldDirty: false,
                        shouldTouch: true,
                      });
                      // 新規 (未保存) は登録時にまとめて保存。既存レコードのみ即時反映。
                      if (isNew || !expenseId) return;
                      try {
                        await dispatch(
                          updateExpense({
                            expense_id: expenseId,
                            body: {
                              status: val as ExpenseRecord["status"],
                            },
                          }),
                        ).unwrap();
                        showToast({
                          type: "success",
                          message: "ステータスを更新しました",
                        });
                      } catch {
                        // 失敗時は元のステータスへ戻す。
                        setValue("status", previous, { shouldDirty: false });
                        showToast({
                          type: "error",
                          message: "ステータスの更新に失敗しました",
                        });
                      }
                    }}
                    inputWidth="100%"
                    fontSize={14}
                    menuWidth={200}
                    placeholder="ステータス"
                  />
                </Box>
              }
            />
          </Box>

          <Box component="form" onSubmit={onSubmit}>
            {formInner}
            <Can action="expense:edit">
              <FixedActionBar
                submitLabel={isNew ? "登録" : "更新"}
                isLoading={submitting}
                disabled={
                  !hasFormDirty &&
                  !localDirty &&
                  !breakdownDirty &&
                  !customFieldsDirty &&
                  !isNew
                }
                embedded={embedded}
              />
            </Can>

            {/* 明細項目の追加モーダル (カテゴリ + 明細項目名)。収入の科目新規登録と同型。 */}
            {addDetailCategoryModal}
          </Box>
        </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default ExpenseDetailPage;
